"use client";

import { useTranslations } from "next-intl";
import Image from "next/image";

const TABLE_ROW_KEYS = [
	{ labelKey: "row1Label", descKey: "row1Desc" },
	{ labelKey: "row2Label", descKey: "row2Desc" },
	{ labelKey: "row3Label", descKey: "row3Desc" },
	{ labelKey: "row4Label", descKey: "row4Desc" },
	{ labelKey: "row5Label", descKey: "row5Desc" },
	{ labelKey: "row6Label", descKey: "row6Desc" },
	{ labelKey: "row7Label", descKey: "row7Desc" },
	{ labelKey: "row8Label", descKey: "row8Desc" },
	{ labelKey: "row9Label", descKey: "row9Desc" },
	{ labelKey: "row10Label", descKey: "row10Desc" },
	{ labelKey: "row11Label", descKey: "row11Desc" },
	{ labelKey: "row12Label", descKey: "row12Desc" },
	{ labelKey: "row13Label", descKey: "row13Desc" },
	{ labelKey: "row14Label", descKey: "row14Desc" },
];

export interface InjectionMoldingCapabilitiesProps {
	translationNamespace?: string;
	/** 左侧注塑模具示意图路径 */
	imageSrc?: string;
	consultHref?: string;
	getQuoteHref?: string;
	/** 表格行对应的 i18n key；不传则使用默认 14 行 */
	tableRowKeys?: readonly { labelKey: string; descKey: string }[];
	/** 是否显示底部 CTA（说明段落 + 双按钮）；Plastic Injection Molding 等页可设为 false */
	showFooterCta?: boolean;
}

export default function InjectionMoldingCapabilities({
	translationNamespace = "injectionMoldingCapabilities",
	imageSrc = "/image/new-version/molding/injection-molding/injection-molding-capabilities.webp",
	consultHref = "/contact-us",
	getQuoteHref = "/upload",
	tableRowKeys = TABLE_ROW_KEYS,
	showFooterCta = true,
}: InjectionMoldingCapabilitiesProps) {
	const t = useTranslations(translationNamespace);
	/** 实际渲染的表格行（默认 14 行，Plastic 页可传入 8 行） */
	const rows = tableRowKeys;

	return (
		<section className="w-full overflow-hidden bg-white">
			<div className="container">
				{/* 标题 + 简介（浅绿白底） */}
				<div className="bg-[#F6FFDE] px-4 pt-8 sm:px-6 sm:pt-10 md:pt-12 lg:px-16 lg:pt-16">
					<div className="mx-auto text-center">
						<h2 className="mb-3 text-2xl font-bold leading-tight text-black sm:mb-4 sm:text-[30px] md:text-4xl">
							{t("title")}
						</h2>
						<p className="text-sm leading-relaxed text-gray-900 md:text-base">{t("intro")}</p>
					</div>
				</div>

				{/* 主内容：左图 + 右表（白底） */}
				<div className="bg-[#F6FFDE] px-4 py-8 sm:px-6 sm:py-10 md:py-12 lg:px-16 lg:py-16">
					<div className="mx-auto max-w-6xl">
						<div className="flex flex-col gap-8 lg:flex-row lg:items-center lg:gap-12">
							{/* 左侧示意图 */}
							<div className="relative h-full w-full shrink-0 lg:w-[42%]">
								<div className="relative flex aspect-[4/3] h-full w-full items-center justify-center lg:aspect-square lg:max-w-md">
									<Image
										src={imageSrc}
										alt={t("imageAlt")}
										fill
										className="bg-[#F6FFDE] object-contain"
										sizes="(max-width: 1024px) 100vw, 42vw"
									/>
								</div>
							</div>
							{/* 右侧规格表 */}
							<div className="min-w-0 flex-1">
								<div className="overflow-x-auto">
									<table className="w-full border-collapse">
										<thead>
											<tr className="border-b border-gray-200">
												<th className="pb-3 pr-4 text-left text-sm font-semibold text-gray-900 sm:pb-4">
													{t("colStandards")}
												</th>
												<th className="pb-3 text-left text-sm font-semibold text-gray-900 sm:pb-4">
													{t("colDescription")}
												</th>
											</tr>
										</thead>
										<tbody>
											{rows.map((row) => (
												<tr key={row.labelKey} className="border-b border-gray-100">
													<td className="py-3 pr-4 align-top text-sm font-medium text-black sm:py-4 lg:w-[45%]">
														{t(row.labelKey)}
													</td>
													{/* whitespace-pre-line：文案中含 \\n 时换行显示 */}
													<td className="whitespace-pre-line py-3 text-sm leading-relaxed text-gray-900 sm:py-4">
														{t(row.descKey)}
													</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
			{/* 底部 CTA：段落 + 双按钮（浅灰绿底）；部分页面关闭 */}
			{showFooterCta ? (
				<div className="border-t border-gray-100 bg-[#FDFEF6] px-4 py-8 sm:px-6 sm:py-10 md:py-12 lg:px-16 lg:py-16">
					<div className="mx-auto max-w-3xl text-center">
						<p className="mb-8 text-sm leading-relaxed text-gray-900 md:text-base">{t("ctaParagraph")}</p>
						<div className="flex flex-wrap items-center justify-center gap-4">
							<a
								href={consultHref}
								target="_blank"
								rel="noopener noreferrer"
								className="inline-flex items-center justify-center rounded-lg bg-themePrimary400 px-6 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-opacity-90 sm:px-8 sm:py-4 sm:text-base"
							>
								{t("consultButton")}
							</a>
							<a
								href={getQuoteHref}
								target="_blank"
								rel="noopener noreferrer"
								className="inline-flex items-center justify-center rounded-lg border-2 border-themePrimary400 bg-white px-6 py-3.5 text-sm font-semibold text-themePrimary400 transition-all duration-300 hover:bg-themePrimary400 hover:text-white sm:px-8 sm:py-4 sm:text-base"
							>
								{t("getQuoteButton")}
							</a>
						</div>
					</div>
				</div>
			) : null}
		</section>
	);
}
